import memoizeAsyncIterable from '@gaia/utils/memoizeAsyncIterable';
import mergeRefs from '@gaia/utils/react/mergeRefs';
import useGetSyncPromise from '@gaia/utils/react/useGetSyncPromise';
import {List, type ListProps} from '@mui/material';
import times from 'lodash/times';
import {createRef, useCallback, useEffect, useRef, useState} from 'react';

import SidebarLoading from './sidebars/SidebarLoading';
// This component efficiently loads and renders items from an "AsyncIterable"

// Items are divided into chunks of 25 called "pages"
const ITEMS_PER_PAGE = 25;

// We render at most 5 pages at a time. Rendering more pages improves scrolling smoothness but
// increases render time.
const RENDER_PAGE_COUNT = 5;

// We prefetch items beyond what's rendered. Fetching is very inexpensive compared to rendering.
const PREFETCH_PAGE_COUNT = 50;

// If the iterable changes, we show the previous iterable for some short time to prevent a white
// flash if the next iterable loads quickly.
const SHOW_PREV_ITERABLE_MS = 500;

interface LazyAsyncListProps<T> extends ListProps {
    iterable: AsyncIterable<T>;
    children: (item: T, itemIndex: number) => React.ReactChild | null;
    empty?: React.ReactChild | React.ReactChildren;
    externalRef: React.Ref<HTMLUListElement>;
}

export default function LazyAsyncList<T>({
    iterable: currentIterable,
    children: renderItem,
    style,
    empty,
    externalRef,
    ...props
}: LazyAsyncListProps<T>) {
    const getSyncPromise = useGetSyncPromise({disableThrashingDetection: true});

    const [firstRenderedPageIndex, setFirstVisiblePageIndex] = useState(0);
    const lastRenderedPageIndex = firstRenderedPageIndex + RENDER_PAGE_COUNT;

    // We store these values as refs because we want them to persist across renders but do not want
    // them to trigger new renders.
    const listElementRef = useRef<HTMLUListElement>(null);
    const pageElementsRef = useRef<React.MutableRefObject<HTMLDivElement>[]>([]);
    const pageHeightsRef = useRef<number[]>([]);

    // When the list scrolls, we must figure which pages ought to be rendered and save the heights
    // of the pages that will no longer be rendered.
    const onScroll = useCallback(() => {
        const nextFirstRenderedPageIndex = pageElementsRef.current.findIndex((ref) => {
            if (!ref.current || !listElementRef.current) return false;
            const offsetBottom = ref.current.offsetTop + ref.current.offsetHeight;
            return offsetBottom > listElementRef.current.scrollTop;
        });

        if (nextFirstRenderedPageIndex !== firstRenderedPageIndex) {
            setFirstVisiblePageIndex(nextFirstRenderedPageIndex);

            pageHeightsRef.current = pageElementsRef.current.map((ref, i) =>
                ref.current && ref.current.offsetHeight
                    ? ref.current.offsetHeight
                    : pageHeightsRef.current[i]
            );
        }
    }, [firstRenderedPageIndex]);

    // We store the previous iterable and loading start time so that we can avoid a white flash
    // if the loading operation completes in just a few miliseconds.
    const prevIterableRef = useRef<AsyncIterable<T> | undefined>();
    const loadingTimeRef = useRef<number | undefined>();

    // Iterables can only be traversed once so we must memoize the values as they come in.
    const getCurrentItem = memoizeAsyncIterable(currentIterable);
    const getPrevItem = prevIterableRef.current && memoizeAsyncIterable(prevIterableRef.current);

    // Prefetch items
    useEffect(() => {
        prefetchLazyAsyncList(currentIterable, firstRenderedPageIndex, getSyncPromise);
    }, [getSyncPromise, firstRenderedPageIndex, currentIterable]);

    const lastItemIndex = (lastRenderedPageIndex + 1) * ITEMS_PER_PAGE - 1;
    const [lastCurrentItem] = getSyncPromise(getCurrentItem(lastItemIndex));
    if (lastCurrentItem) {
        prevIterableRef.current = currentIterable;
        loadingTimeRef.current = undefined;
    } else if (!lastCurrentItem && !loadingTimeRef.current) {
        loadingTimeRef.current = Date.now();
    }

    const showPrevItems =
        !lastCurrentItem &&
        getPrevItem &&
        loadingTimeRef.current &&
        Date.now() - loadingTimeRef.current < SHOW_PREV_ITERABLE_MS;

    const getItem = showPrevItems ? getPrevItem! : getCurrentItem;

    const [lastItem] = getSyncPromise(getItem(lastItemIndex));
    const [firstItem] = getSyncPromise(getItem(0));

    return (
        <List
            ref={mergeRefs(listElementRef, externalRef)}
            disablePadding
            onScroll={onScroll}
            style={{
                ...style,
                position: 'relative',
                overflowY: 'auto',
                maxHeight: '100%',
            }}
            {...props}
        >
            {firstItem?.done && empty}

            {times(Math.max(pageHeightsRef.current.length, lastRenderedPageIndex), (pageIndex) => {
                if (!pageElementsRef.current[pageIndex]) {
                    pageElementsRef.current[pageIndex] = createRef<HTMLDivElement>() as any;
                }

                if (pageIndex >= firstRenderedPageIndex && pageIndex <= lastRenderedPageIndex) {
                    return (
                        <div key={pageIndex} ref={pageElementsRef.current[pageIndex]}>
                            {times(ITEMS_PER_PAGE, (i) => {
                                const itemIndex = pageIndex * ITEMS_PER_PAGE + i;
                                const [item] = getSyncPromise(getItem(itemIndex));
                                if (!item || item.done) return null;
                                else return renderItem(item.value, itemIndex);
                            })}
                        </div>
                    );
                } else {
                    // Create an empty spacer div for pages which are not rendered.
                    return (
                        <div
                            key={pageIndex}
                            ref={pageElementsRef.current[pageIndex]}
                            style={{
                                height: pageHeightsRef.current[pageIndex],
                            }}
                        />
                    );
                }
            })}

            {(!lastItem || !lastItem.done) && <SidebarLoading />}
        </List>
    );
}

export function prefetchLazyAsyncList<T>(
    iterable: AsyncIterable<T>,
    firstRenderedPageIndex = 0,
    getSyncPromise: (t: any) => void = () => {}
) {
    const getItem = memoizeAsyncIterable(iterable);
    times(PREFETCH_PAGE_COUNT, (i) =>
        times(ITEMS_PER_PAGE, (j) => {
            const pageIndex = i + firstRenderedPageIndex;
            const itemIndex = pageIndex * ITEMS_PER_PAGE + j;

            // Pre-seed the "getSyncPromise" cache to avoid a one tick delay later.
            getSyncPromise(getItem(itemIndex));
        })
    );
}
